import React from 'react';
import styled, { css } from 'styled-components';
import { borders, colors, spaces } from '../../settings';

export interface ProgressBarProps {
    /**
     * Number from [0,100] indicating the percentage progress that should be displayed.
     */
    progress: number;
    /**
     * Set to display a smaller (in height) progress bar.
     */
    small?: boolean;
    /**
     * The background color for the progress bar. Either a string value representing one of our color constants
     * (i.e. `"karlTheFog"`) or a valid CSS `background` value as a string, including gradient or image values.
     */
    background?: string;
    /**
     * The foreground color for the progress bar. Either a string value representing one of our color constants
     * (i.e. `"karlTheFog"`) or a valid CSS `background` value as a string, including gradient or image values.
     */
    color?: string;
}

const ProgressBarContainer = styled.div<Omit<ProgressBarProps, 'progress'>>`
    position: relative;
    width: 100%;
    overflow: hidden;
    z-index: 1;

    ${({ background }) => css`
        background: ${colors[background as colors.Color] || background};
    `}

    ${({ small }) => css`
        height: ${small ? spaces.space4 : spaces.space6};
        border-radius: ${small ? borders.borderRadiuses.small : borders.borderRadiuses.medium}px;
    `}
`;

const ProgressBarFill = styled.div<ProgressBarProps>`
    transition: width 0.3s ease-out;
    height: 100%;

    ${({ color }) => css`
        background: ${colors[color as colors.Color] || color};
    `}

    ${({ progress }) => css`
        width: ${progress}%;
    `}

    ${({ small }) => css`
        border-radius: ${small ? borders.borderRadiuses.small : borders.borderRadiuses.medium}px;
    `}
`;

/**
 * Displays a 100% wide horizontal bar with an inner bar used to indicate the percentage progress that a process is
 * complete.
 */
export function ProgressBar({
    progress,
    small = false,
    background = colors.pebbleBeach,
    color = colors.puntRoad,
}: ProgressBarProps) {
    return (
        <ProgressBarContainer
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            small={small}
            background={background}
        >
            <ProgressBarFill color={color} small={small} progress={progress} />
        </ProgressBarContainer>
    );
}
